// 用電頁（plan SPEC NRG-V1）：選單「設備與場域 › 用電」（/energy）；頁內分頁 總覽｜用電排行｜報表；右上〔用電設定〕跳到範圍內的{leaf}（多個時先選）。
// 選單不出現的人直接開網址 →「只有能看用電的人能開這頁」；範圍內沒有量測點 → 空狀態（〈範圍名〉沒有會回報用電的設備…〔切到全部〕）。
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Route, Routes, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScope } from '../../lib/scope';
import { useDismiss } from '../../components/useDismiss';
import { useEnergyEnv, useEnergyNav } from './hooks';
import { MockBanner } from './parts';
import { Overview } from './Overview';
import { Ranking } from './Ranking';
import { Report } from './Report';

const NOTICE_KEY = 'vms.energy.notice_seen';

export function EnergyPage() {
  const { t } = useTranslation();
  useEnergyEnv();
  const visible = useEnergyNav();
  const { booting } = useScope();
  if (!booting && !visible)
    return (
      <div className="page en-root">
        <h1 className="page-title">{t('energy:title')}</h1>
        <p className="muted" data-testid="en-no-access">
          {t('energy:no_access_page')}
        </p>
      </div>
    );
  return (
    <div className="page en-root km">
      <div className="en-page">
        <div className="en-head">
          <div>
            <h1 className="page-title">{t('energy:title')}</h1>
            <p className="en-sub">{t('energy:subtitle')}</p>
          </div>
          <div className="spacer" />
          <SettingsButton />
        </div>
        <nav className="km-tabs" aria-label={t('energy:title')}>
          <NavLink to="/energy" end data-testid="en-tab-overview">
            {t('energy:tab.overview')}
          </NavLink>
          <NavLink to="/energy/ranking" data-testid="en-tab-ranking">
            {t('energy:tab.ranking')}
          </NavLink>
          <NavLink to="/energy/report" data-testid="en-tab-report">
            {t('energy:tab.report')}
          </NavLink>
        </nav>
        <MockBanner />
        <FirstNotice />
        <Routes>
          <Route index element={<Overview />} />
          <Route path="ranking" element={<Ranking />} />
          <Route path="report" element={<Report />} />
        </Routes>
      </div>
    </div>
  );
}

/** 第一次開用電頁的告知（只顯示一次，資料最小化的說明；設計稿 v01b）。 */
function FirstNotice() {
  const { t } = useTranslation();
  const [seen, setSeen] = useState(() => {
    try {
      return localStorage.getItem(NOTICE_KEY) === '1';
    } catch {
      return true;
    }
  });
  if (seen) return null;
  return (
    <div className="km-bar" role="note" data-testid="en-first-notice">
      <span className="ic" aria-hidden>
        ⓘ
      </span>
      <span className="txt small">{t('energy:first_notice')}</span>
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => {
          setSeen(true);
          try {
            localStorage.setItem(NOTICE_KEY, '1');
          } catch {
            /* 無 storage */
          }
        }}
      >
        {t('energy:dismiss')}
      </button>
    </div>
  );
}

/** 〔用電設定〕：範圍內自己是 owner / admin 的{leaf}；一個直接去、多個先選；沒有（只有看用電的委派）不畫。 */
function SettingsButton() {
  const { t } = useTranslation();
  const { visible } = useScope();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useDismiss(open, () => setOpen(false), wrap);
  const homes = visible.filter((h) => !h.external && (h.role === 'owner' || h.role === 'admin'));
  useEffect(() => setOpen(false), [visible]);
  if (!homes.length) return null;
  const go = () => (homes.length === 1 ? nav(`/sites/energy/${encodeURIComponent(homes[0].home_id)}`) : setOpen((v) => !v));
  return (
    <div className="en-pop-anchor" ref={wrap}>
      <button type="button" className="btn" onClick={go} aria-expanded={homes.length > 1 ? open : undefined} data-testid="en-settings-btn">
        <span aria-hidden>⚙</span> {t('energy:settings_btn')}
      </button>
      {open && (
        <div className="ot-menu" role="menu" aria-label={t('energy:settings_pick')} style={{ maxHeight: 360, overflow: 'auto' }}>
          <div className="small muted" style={{ padding: '6px 12px' }}>
            {t('energy:settings_pick')}
          </div>
          {homes.map((h) => (
            <Link key={h.home_id} role="menuitem" style={{ padding: '8px 12px' }} to={`/sites/energy/${encodeURIComponent(h.home_id)}`}>
              {h.display_name ?? h.home_id}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

